import { logError } from '@/lib/log-error';
import { useRef, useState } from 'react';

import { useApprovalCardStatus } from '../approval-card-status-context';

import {
  ComposerDecision,
  type ComposerGate,
  useComposerTurnKey,
  useCurrentComposerTurn,
} from '../composer-gate-context';
import { DecisionFooter, type DecisionFooterProps } from './decision-footer';

import type {
  CardEnvelope,
  ConfirmationSelection,
} from '@borradh-workspace/contracts/claire';

/**
 * The SHARED wire contract, not a hand-mirrored interface. This component
 * used to declare its own shape and the two sides only agreed because they
 * happened to spell the same fields — the drift class
 * `@borradh-workspace/contracts/claire` closes.
 */
export type ConfirmationRequiredPresentation = Extract<
  CardEnvelope,
  { type: 'confirmation_required' }
>;

/**
 * The card for a destructive action the factory wants confirmed.
 *
 * WHY THIS IS GENERIC. The tool factory already returns a
 * `confirmation_required` presentation for EVERY destructive tool — the
 * summary, the action, the token — and the app rendered none of it. Confirming
 * happened in prose: Claire described what she was about to do, the owner
 * typed yes, and she called again with the token. That works, but it means the
 * one moment where something irreversible is about to happen looks exactly like
 * every other paragraph in the chat.
 *
 * Dispatching on the ENVELOPE rather than the tool name is the point. The
 * alternative — a branch per tool, as `queueVideoExport` and the `confirm*`
 * tools each have — is why `regenerateItem` shipped with Claire announcing
 * "confirm on the card above" over a card that did not exist. Every future
 * destructive tool gets this one for free.
 *
 * Approving SENDS A MESSAGE rather than calling the tool directly. The second
 * call has to carry the confirmation token, and the model is what holds it —
 * the same reason the clip editor stages on the server rather than in React.
 */
export function ConfirmationRequiredCard({
  id,
  presentation,
  onConfirm,
}: {
  /** The tool call this card renders from — a stable id for gate registration. */
  id: string;
  presentation: ConfirmationRequiredPresentation;
  /** False means chat refused the send; void preserves legacy callbacks. */
  onConfirm: (
    text: string,
    selection?: ConfirmationSelection
  ) => boolean | void | Promise<boolean> | Promise<void>;
}) {
  const [answered, setAnswered] = useState<'yes' | 'no' | null>(null);
  const [pending, setPending] = useState(false);
  const deliveryState = useRef<'idle' | 'pending' | 'answered'>('idle');
  const { summary } = presentation;
  const title = summary?.campaignPackage
    ? `Launch campaign with ads: ${summary.campaignPackage.ads.map((ad) => ad.title).join(', ')}`
    : (summary?.title ?? 'Confirm this action?');
  const summaryFields = summary?.campaignPackage ? undefined : summary?.fields;
  const currentTurn = useCurrentComposerTurn();
  const turnKey = useComposerTurnKey();
  const approvals = useApprovalCardStatus();
  // A card from an earlier turn stays pressable while the server says nobody
  // has answered it (see `ApprovalCardStatusProvider`); otherwise it is gone
  // from history, as before.
  const earlierStatus =
    !currentTurn && approvals && presentation.token
      ? approvals.statusOf(presentation.token)
      : 'closed';
  const stillWaiting = !currentTurn && earlierStatus !== 'closed';
  // Past its lifetime, approving cannot run the action; the server re-stages
  // it as a fresh card instead, so the button says what will happen.
  const expired =
    earlierStatus === 'expired' ||
    (currentTurn && Date.parse(presentation.expiresAt) <= Date.now());
  const acceptLabel = expired
    ? 'Ask again'
    : (presentation.confirmLabel ?? 'Confirm');
  // Keep the saved proposal in history without treating a later reply (or a
  // sent approval) as launch proof. Its ad previews stay in the chat separately.
  const readOnlyPackage =
    !!summary?.campaignPackage &&
    ((!currentTurn && !stillWaiting) || answered !== null);

  const sendDecision = async (
    answer: 'yes' | 'no',
    reply: string,
    selection?: ConfirmationSelection
  ): Promise<boolean> => {
    // Both the inline and composer controls can call this before React paints
    // the disabled state. One unresolved send must own the decision.
    if (deliveryState.current !== 'idle') return false;
    deliveryState.current = 'pending';
    setPending(true);
    try {
      const accepted = await (selection
        ? onConfirm(reply, selection)
        : onConfirm(reply));
      if (accepted !== false) {
        deliveryState.current = 'answered';
        setAnswered(answer);
        return true;
      }
    } catch (error) {
      logError('assistant.confirmation.send', error, { feature: 'assistant' });
    } finally {
      if (deliveryState.current === 'pending') deliveryState.current = 'idle';
      setPending(false);
    }
    return false;
  };
  // A sibling answered by another card's "Approve all" / "Cancel all": close
  // this card too, so it cannot come back as a smaller batch of used tokens.
  const closeAsAnswered = (answer: 'yes' | 'no') => {
    if (deliveryState.current !== 'idle') return;
    deliveryState.current = 'answered';
    setAnswered(answer);
  };

  // Cancel also closes the approval on the server, so the card cannot come
  // back after a reload (or be run later) as if it were still waiting.
  const declineOnServer = (tokens: string[]) => {
    if (!approvals) return;
    for (const token of tokens) {
      approvals.decline(token).catch((error: unknown) => {
        logError('assistant.confirmation.decline', error, {
          feature: 'assistant',
        });
      });
    }
  };
  const handleReject = () => {
    if (presentation.token) declineOnServer([presentation.token]);
    void sendDecision('no', "No — don't do that.");
  };
  // An earlier card the owner no longer wants: close it quietly. Claire has
  // moved on, so a "no" message would only start a turn about nothing.
  const handleDismiss = () => {
    if (presentation.token) declineOnServer([presentation.token]);
    deliveryState.current = 'answered';
    setAnswered('no');
  };
  const selection: ConfirmationSelection | undefined =
    presentation.token &&
    presentation.executeToolName &&
    presentation.resourceId
      ? {
          token: presentation.token,
          toolName: presentation.executeToolName,
          resourceId: presentation.resourceId,
        }
      : undefined;
  const handleAccept = () => {
    const reply = expired
      ? `Please prepare “${title}” again for my approval.`
      : summary?.campaignPackage
        ? `Yes, approve and launch “${summary.campaignPackage.campaign.title}”.`
        : stillWaiting
          ? `Yes, go ahead with “${title}”.`
          : 'Yes, go ahead.';
    if (selection) {
      void sendDecision('yes', reply, selection);
    } else {
      void sendDecision('yes', reply);
    }
  };

  // A campaign launch keeps its own full review; every other bound card can
  // be approved together with the other cards of the same turn (ENG-1945).
  const batchItem: ConfirmationBatchItem | undefined =
    selection && !summary?.campaignPackage
      ? {
          selection,
          title,
          fields: summaryFields,
          restagedNote: presentation.restagedNote,
          busy: pending,
          approveAll: (all, reply) => sendDecision('yes', reply, all),
          rejectAll: (reply) => {
            declineOnServer([
              selection.token,
              ...(selection.batch ?? []).map((card) => card.token),
            ]);
            return sendDecision('no', reply);
          },
          closeAsAnswered,
        }
      : undefined;

  const composerGate: ComposerGate | null =
    answered === null && !readOnlyPackage
      ? {
          sentence: title,
          onAccept: handleAccept,
          onReject: handleReject,
          acceptLabel,
          rejectLabel: 'Cancel',
          busy: pending,
          ...(batchItem
            ? {
                batch: {
                  // Only cards from the same assistant turn combine; the
                  // server checks the same thing (`sameTurnAs`).
                  key: `confirmation:${turnKey}`,
                  item: batchItem,
                  combine: (items) =>
                    combineConfirmations(items as ConfirmationBatchItem[]),
                },
              }
            : {}),
        }
      : null;

  if (answered !== null && !readOnlyPackage) return null;

  if (stillWaiting && !readOnlyPackage) {
    // Inline, not in the composer: the composer belongs to the current turn.
    return (
      <div
        data-testid="claire-card-confirmation-waiting"
        className="w-full rounded-lg border bg-card p-4 sm:max-w-lg"
      >
        <p className="mb-1 text-[11px] text-muted-foreground">
          {expired
            ? 'This approval expired, so nothing was changed. Ask again and Claire will prepare it for you to approve.'
            : 'Still waiting for your approval.'}
        </p>
        <p className="text-sm font-medium">{title}</p>
        {summaryFields?.length && !expired ? (
          <SummaryFields fields={summaryFields} />
        ) : null}
        <div className="mt-3">
          <DecisionFooter
            hideSentence
            sentence={title}
            onAccept={handleAccept}
            onReject={handleDismiss}
            acceptLabel={acceptLabel}
            rejectLabel={expired ? 'Dismiss' : 'Cancel'}
            busy={pending || !approvals?.canAnswer}
          />
        </div>
      </div>
    );
  }

  return (
    <ComposerDecision id={id} gate={composerGate}>
      <div
        data-testid={
          readOnlyPackage
            ? 'claire-card-campaign-package-review'
            : 'claire-card-confirmation-required'
        }
        className="w-full rounded-lg border bg-card p-4 sm:max-w-lg"
      >
        {expired && !readOnlyPackage ? (
          <p className="mb-1 text-[11px] text-muted-foreground">
            This approval expired, so nothing was changed. Ask again and Claire
            will prepare it for you to approve.
          </p>
        ) : presentation.restagedNote ? (
          <p className="mb-1 text-[11px] text-muted-foreground">
            {presentation.restagedNote}
          </p>
        ) : null}

        <p className="text-sm font-medium">{title}</p>

        {summaryFields?.length ? (
          <SummaryFields fields={summaryFields} />
        ) : null}

        {!readOnlyPackage ? (
          <div className="mt-3">
            <DecisionFooter
              hideSentence
              sentence={title}
              onAccept={handleAccept}
              onReject={handleReject}
              acceptLabel={acceptLabel}
              rejectLabel="Cancel"
              busy={pending}
            />
          </div>
        ) : null}
      </div>
    </ComposerDecision>
  );
}

function SummaryFields({
  fields,
}: {
  fields: Array<{ label: string; value: string }>;
}) {
  return (
    <dl className="mt-2 space-y-1">
      {fields.map((field) => (
        <div
          key={field.label}
          className="flex items-baseline justify-between gap-3 text-xs"
        >
          <dt className="shrink-0 text-muted-foreground">{field.label}</dt>
          <dd className="min-w-0 whitespace-pre-wrap break-words text-right">
            {field.value}
          </dd>
        </div>
      ))}
    </dl>
  );
}

interface ConfirmationBatchItem {
  selection: ConfirmationSelection;
  title: string;
  fields?: Array<{ label: string; value: string }>;
  restagedNote?: string;
  busy: boolean;
  /** Resolves true once the message carrying the decision was sent. */
  approveAll: (
    selection: ConfirmationSelection,
    reply: string
  ) => Promise<boolean>;
  rejectAll: (reply: string) => Promise<boolean>;
  closeAsAnswered: (answer: 'yes' | 'no') => void;
}

/**
 * One decision for every approval card the last turn raised (ENG-1945).
 *
 * A bulk request ("a £25 deposit on all five services", "move the logo on
 * both ads") raises one card per item. The composer used to show only the
 * newest, so one click approved one item and Claire came back for the rest,
 * turn after turn. Every pending card is listed here and one click approves
 * them all: the newest card sends the message, carrying the others as its
 * selection's `batch`.
 */
function combineConfirmations(
  items: ConfirmationBatchItem[]
): DecisionFooterProps {
  const sender = items[items.length - 1];
  const count = items.length;
  const siblings = items.slice(0, -1);
  const closeSiblings = (answer: 'yes' | 'no') => (sent: boolean) => {
    if (sent) for (const item of siblings) item.closeAsAnswered(answer);
  };
  const onAccept = () => {
    void sender
      .approveAll(
        {
          ...sender.selection,
          batch: siblings.map((item) => item.selection),
        },
        `Yes, go ahead with all ${count}.`
      )
      .then(closeSiblings('yes'));
  };
  const onReject = () => {
    void sender
      .rejectAll("No — don't make any of these changes.")
      .then(closeSiblings('no'));
  };
  const busy = items.some((item) => item.busy);
  const footer = {
    sentence: `Approve all ${count} changes?`,
    onAccept,
    onReject,
    acceptLabel: `Approve all ${count}`,
    rejectLabel: 'Cancel all',
    busy,
  };
  return {
    ...footer,
    content: (
      <div
        data-testid="claire-card-confirmation-batch"
        className="w-full rounded-lg border bg-card p-4"
      >
        <p className="text-sm font-medium">
          {count} changes are waiting for your approval
        </p>
        <ol className="mt-2 space-y-2">
          {items.map((item) => (
            <li
              key={item.selection.token}
              className="rounded-md border bg-background p-2"
            >
              {item.restagedNote ? (
                <p className="mb-1 text-[11px] text-muted-foreground">
                  {item.restagedNote}
                </p>
              ) : null}
              <p className="text-xs font-medium">{item.title}</p>
              {item.fields?.length ? (
                <dl className="mt-1 space-y-0.5">
                  {item.fields.map((field) => (
                    <div
                      key={field.label}
                      className="flex items-baseline justify-between gap-3 text-xs"
                    >
                      <dt className="shrink-0 text-muted-foreground">
                        {field.label}
                      </dt>
                      <dd className="min-w-0 whitespace-pre-wrap break-words text-right">
                        {field.value}
                      </dd>
                    </div>
                  ))}
                </dl>
              ) : null}
            </li>
          ))}
        </ol>
        <div className="mt-3">
          <DecisionFooter hideSentence {...footer} />
        </div>
      </div>
    ),
  };
}
